import { Directive, ElementRef, EventEmitter, Input, Output } from "@angular/core";
import { Router } from "@angular/router";
import { isInIframe } from "@app/helpers/iframe-helper";
import { AlertDisplayType } from '@commonEnums/AlertDisplayType';
import { delayHelper } from "@commonHelpers/delay-helper";
import { isNullish } from "@commonHelpers/math-utils";
import { AlertService } from '@commonServices/alert.service';
import { AlertPositionType } from "@enums/alertPositionType";
import { ViewType } from "@enums/view-type";
import { CatalogViewRelatedCatalogsDto } from "@interfaces/HttpClient/CatalogApiPublicModels";
import { CatalogOrCatalogItemSuggestionsDto, CatalogSuggestionDto, SearchFilterId } from "@interfaces/HttpClient/SearchApiModels";
import { BreadcrumbService } from "@services/breadcrumb.service";
import { SearchbarService } from "@services/searchbar.service";
import { TranslationService } from "@services/translation.service";
import { ViewTypeService } from "@services/view-type.service";
import { Subscription } from "rxjs";

@Directive()
export abstract class SearchbarBase {
	public currentSearchWord = '';
	public currentSearchTerm = '';
	private insertIntoSearchbarSubscription: Subscription;
	private markSearchbarSubscription: Subscription;

	public additionalItems: AdditionalSearchItem[] = [];
	public indexedAdditionalId: number;
	private currentViewType$ = this.viewTypeService.currentViewType$
	public currentViewType: ViewType
	public viewTypeSubscription: Subscription
	public suggestions?: CatalogOrCatalogItemSuggestionsDto;
	public suggestedTerms: string[];
	public suggestedCatalogs: CatalogSuggestionDto[];
	protected enableBlur = true;
	public indexedSearchterm;
	public indexedCatalogKey;
	protected searchSuggestionIdx;
	private touchMoving = false;
	protected isPreview = false;
	private closeMenuFunction = () => this.hasBlur(true, true);
	private touchMovingFunction = () => this.touchMoving = true;

	constructor(
		protected translationService: TranslationService,
		protected breadcrumbService: BreadcrumbService,
		protected router: Router,
		protected searchbarService: SearchbarService,
		protected elRef: ElementRef,
		protected viewTypeService: ViewTypeService,
		protected alertService?: AlertService<AlertPositionType>,
	) {
	}

	protected readonly SUGGESTIONCOUNT = 7;
	protected readonly CATALOGCOUNT = 3;

	@Input() searchbarIsVisible = false;
	@Input() categoryGroups: { title: string, id: number }[];
	@Output() emptySearch = new EventEmitter();
	@Output() initSearchbar = new EventEmitter();

	protected init() {
		window.addEventListener('mouseup', this.closeMenuFunction);
		window.addEventListener('touchend', this.closeMenuFunction)
		window.addEventListener('touchcancel', this.closeMenuFunction);
		window.addEventListener('touchmove', this.touchMovingFunction);


		this.insertIntoSearchbarSubscription = this.searchbarService.getSearchInsert().subscribe(async term => {
			this.currentSearchWord = this.setSearchTerm(term)
		});
		this.markSearchbarSubscription = this.searchbarService.getSearchMark().subscribe(() => {
			this.elRef.nativeElement.querySelector(`#searchbar`)?.select()
		})
		this.viewTypeSubscription = this.currentViewType$.subscribe(currentViewType => {
			this.currentViewType = currentViewType
		})
		this.initSearchbar.emit();
	}

	protected destroy() {
		window.removeEventListener('mouseup', this.closeMenuFunction)
		window.removeEventListener('touchend', this.closeMenuFunction)
		window.removeEventListener('touchcancel', this.closeMenuFunction)
		window.removeEventListener('touchmove', this.touchMovingFunction);
		this.insertIntoSearchbarSubscription?.unsubscribe();
		this.markSearchbarSubscription?.unsubscribe();
		this.viewTypeSubscription?.unsubscribe();
	}

	protected setSearchTerm(term: string): string {
		return term === undefined || term === null ? "" : term;
	}

	protected trackBeforeNavigate() {
	}

	public getCatalogCategories(catalog: CatalogViewRelatedCatalogsDto): string {
		return catalog.categoryTitleResourceKeys?.map(category => this.translationService.getByKey(category))
			.join(" | ") ?? ' ';
	}

	public clearSearch() {
		this.searchbarService.searchTerm = "";
		this.emptySearch.emit();
		this.currentSearchWord = "";
	}

	protected abstract navigateToSearch(term, isInput?: boolean);
	public abstract navigateToCatalog(key?: string)

	async onKeyDown(event: KeyboardEvent) {
		if (event.code === 'Escape') {
			this.currentSearchWord = this.currentSearchTerm;
			this.closeSuggestions();
			return;
		}
		// Die Erkennung von Mobile Enter funktioniert über event.key
		if (event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter') {
			if (!isNullish(this.currentSearchWord) && this.currentSearchWord !== "") {

				this.trackSearchSuggestionSourceChanged();

				await this.navigateToSearch(this.currentSearchWord);
			} else if (!isNullish(this.indexedCatalogKey)) {
				await this.navigateToCatalog(this.indexedCatalogKey);

			} else if (!isNullish(this.indexedAdditionalId)) {
				const additionalItem = this.additionalItems.find(item => item.id === this.indexedAdditionalId);
				if (!isNullish(additionalItem)) {
					await this.handleAdditionalItem(additionalItem);
				}

			}
			this.closeSuggestions();
		}
		if (event.code === 'ArrowDown') {
			if (isNullish(this.searchSuggestionIdx)) {
				this.searchSuggestionIdx = 0
			} else if (this.searchSuggestionIdx < ((this.suggestedTerms?.length ?? 0) + (this.suggestedCatalogs?.length ?? 0) + (this.additionalItems?.length ?? 0) - 1)) {
				this.searchSuggestionIdx++;
			}

		}

		if (event.code === 'ArrowUp') {
			if (!isNullish(this.searchSuggestionIdx) && this.searchSuggestionIdx > 0) {
				this.searchSuggestionIdx--;
			}
		}

		if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
			this.indexedSearchterm = this.suggestedTerms?.[this.searchSuggestionIdx];
			this.indexedAdditionalId = undefined;
			if (isNullish(this.indexedSearchterm)) {
				this.indexedCatalogKey = this.suggestedCatalogs?.[this.searchSuggestionIdx - (this.suggestedTerms?.length ?? 0)]?.key
				this.currentSearchWord = undefined;

				if (isNullish(this.indexedCatalogKey)) {
					this.indexedAdditionalId = this.additionalItems?.[this.searchSuggestionIdx - (this.suggestedTerms?.length ?? 0) - (this.suggestedCatalogs?.length ?? 0)]?.id;
				}
			} else {

				this.indexedCatalogKey = undefined;
				this.currentSearchWord = this.indexedSearchterm;
				delayHelper(() => {
					const input = this.elRef.nativeElement.querySelector('.searchbar-input');
					const idx = this.currentSearchWord?.toLocaleUpperCase().indexOf(this.currentSearchTerm?.toLocaleUpperCase())
					if (idx > -1) {
						input?.setSelectionRange(idx + this.currentSearchTerm?.length, this.currentSearchWord?.length);
					}

				})
			}

		} else {
			this.indexedSearchterm = undefined;
			this.indexedCatalogKey = undefined;
			this.searchSuggestionIdx = undefined;
		}

	}

	protected focusToInput() {
		this.enableBlur = true;
		this.elRef.nativeElement.querySelector('.searchbar-input')?.focus();
	}

	public closeSuggestions() {
		if (this.enableBlur) {
			this.suggestions = undefined;
			this.suggestedTerms = undefined;
			this.suggestedCatalogs = undefined;
			this.indexedSearchterm = undefined;
			this.indexedCatalogKey = undefined;
			this.additionalItems = undefined;
		}

	}

	public hasBlur(value: boolean, isMouseUp?: boolean) {
		if (isMouseUp && value && !this.touchMoving) {
			this.closeSuggestions();
		}
		this.enableBlur = value;
		this.touchMoving = false;
	}


	protected trackSearchSuggestionSourceChanged() {
	}

	protected trackSearchSuggestionHistory() {
	}

	protected trackSuggestionSourceChangedParams() {
	}

	protected trackGlobalSuggestionSourceChangedParams() {
	}


	protected setCatalogViewOrigin() {
	}

	protected async searchnavigate(term: string, searchFilter: string) {
		await this.router.navigate(["search"], {
			queryParams: {
				searchTerm: term,
				searchFilter,
				searchPage: 1
			}
		});
	}

	public getSearchCatalogCategories(catalog: CatalogSuggestionDto): string {
		return catalog.suggestedCategoryGroups?.flatMap(cg => cg.suggestedCategories)
			.map(category => this.translationService.getByKey(category.titleResourceKey))
			.join(" | ") ?? ' ';
	}



	public async handleAdditionalItem(item: AdditionalSearchItem) {
		if (this.isPreview) {
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Warning, this.translationService.getByKey("preview.notify-message"))
			return
		}
		this.trackSearchSuggestionHistory();

		const searchTerm = this.currentSearchWord ?? this.currentSearchTerm
		if (item.functionality === 'category') {
			this.trackSuggestionSourceChangedParams();
			if (isInIframe()) {
				window.open(`/search?searchFilter=${SearchFilterId.Category}:G${item.id}&searchTerm=${searchTerm}`, '_blank')
			} else {
				await this.router.navigate(["search"], {
					queryParams: {
						searchFilter: SearchFilterId.Category + ':G' + item.id,
						searchTerm
					}
				});
			}


		}
		if (item.functionality === 'global') {
			this.trackGlobalSuggestionSourceChangedParams();

			if (isInIframe()) {
				window.open(`/search?searchTerm=${searchTerm}`, '_blank')
			} else {
				await this.router.navigate(["search"], {
					queryParams: { searchTerm }
				});
			}

		}

	}
}

export interface AdditionalSearchItem {
	title: string,
	id?: number,
	logo: string,
	description: string,
	functionality: string
}
